import { LeadFile } from '../types'
import { supabase, BUCKETS } from './supabaseClient'
import { getDb, refresh } from './store'

/**
 * Archivos adjuntos a un lead. Se suben al bucket privado `lead-files` con
 * la ruta `organization_id/archivo` para que las políticas de acceso
 * funcionen, y se registran en `lead_activities`.
 */
export function listFiles(leadId: string): LeadFile[] {
  return getDb()
    .files.filter((f) => f.leadId === leadId)
    .sort((a, b) => b.createdAt.localeCompare(a.createdAt))
}

export async function addFile(
  organizationId: string,
  leadId: string,
  file: File,
  uploadedBy: string,
): Promise<void> {
  const path = `${organizationId}/${Date.now()}-${file.name}`
  const { error: uploadError } = await supabase.storage
    .from(BUCKETS.leadFiles)
    .upload(path, file, { upsert: false })
  if (uploadError) throw new Error(uploadError.message)

  const { data } = await supabase.auth.getUser()
  const { error } = await supabase.from('lead_activities').insert({
    organization_id: organizationId,
    lead_id: leadId,
    type: 'archivo',
    description: `Archivo adjuntado: ${file.name}`,
    created_by: data.user?.id ?? null,
    metadata: {
      path,
      name: file.name,
      size: file.size,
      type: file.type,
      uploadedBy,
    },
  })
  if (error) throw new Error(error.message)
  await refresh()
}

export async function deleteFile(fileId: string): Promise<void> {
  const file = getDb().files.find((f) => f.id === fileId)
  const { data } = await supabase
    .from('lead_activities')
    .select('metadata')
    .eq('id', fileId)
    .maybeSingle()
  const path = (data?.metadata as { path?: string } | null)?.path
  if (path) await supabase.storage.from(BUCKETS.leadFiles).remove([path])
  const { error } = await supabase.from('lead_activities').delete().eq('id', fileId)
  if (error) throw new Error(error.message)
  if (file) await refresh()
}

/** Sube el logotipo o el favicon de una organización a su bucket público. */
export async function uploadBrandAsset(
  organizationId: string,
  file: File,
  kind: 'logo' | 'favicon',
): Promise<string> {
  const bucket = kind === 'logo' ? BUCKETS.logos : BUCKETS.favicons
  const path = `${organizationId}/${Date.now()}-${file.name}`
  const { error } = await supabase.storage.from(bucket).upload(path, file, {
    upsert: true,
  })
  if (error) throw new Error(error.message)
  return supabase.storage.from(bucket).getPublicUrl(path).data.publicUrl
}

/** Sube una imagen usada dentro de una landing. */
export async function uploadLandingAsset(
  organizationId: string,
  file: File,
): Promise<string> {
  const path = `${organizationId}/${Date.now()}-${file.name}`
  const { error } = await supabase.storage
    .from(BUCKETS.landingAssets)
    .upload(path, file, { upsert: true })
  if (error) throw new Error(error.message)
  return supabase.storage.from(BUCKETS.landingAssets).getPublicUrl(path).data
    .publicUrl
}
